import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import {
    Box,
    Typography,
    Button,
} from "@mui/material";

import {
    School,
    BusinessCenter,
    Groups,
    EmojiObjects,
    WorkspacePremium,
    AutoAwesome,
    CheckCircle,
    ArrowForward,
} from "@mui/icons-material";

import student from "./headerteacher.jpg";
import ApplyForEntranceForm from "./ApplyForEntranceForm";

const COLORS = {
    black: "#070908",
    deepBlack: "#040605",
    blackSoft: "#111512",

    greenDark: "#0B5D2A",
    green: "#16803A",
    greenBright: "#20A84A",

    lemon: "#C8F23A",
    lemonBright: "#B7F000",

    yellow: "#F5D32F",
    yellowBright: "#FFE45A",

    white: "#FFFFFF",
    cream: "#F7FAF5",

    text: "#111613",
    muted: "#68736C",

    border: "#DDE5DE",
    paleGreen: "#EFF8F1",
    paleYellow: "#FFFBE7",
};

const FloatingIcon = ({
    Icon,
    top,
    left,
    right,
    bottom,
    delay = 0,
    size = 27,
}) => (
    <motion.div
        initial={{
            opacity: 0,
            y: 30,
            scale: 0.7,
        }}
        animate={{
            opacity: [0, 0.72, 0.4, 0],
            y: [25, -5, -35, -70],
            x: [0, 12, -7, 14],
            rotate: [0, 10, -8, 14],
            scale: [0.7, 1, 1.05, 0.65],
        }}
        transition={{
            duration: 8,
            delay,
            repeat: Infinity,
            ease: "easeInOut",
        }}
        style={{
            position: "absolute",
            top,
            left,
            right,
            bottom,
            zIndex: 4,
            pointerEvents: "none",
        }}
    >
        <Icon
            sx={{
                fontSize: size,
                color: COLORS.lemon,
                opacity: 0.62,
                filter:
                    "drop-shadow(0 0 12px rgba(200,242,58,.22))",
            }}
        />
    </motion.div>
);

const Glow = ({
    size = 400,
    top,
    left,
    right,
    bottom,
    color = "rgba(32,168,74,.18)",
}) => (
    <motion.div
        animate={{
            scale: [0.94, 1.08, 0.94],
            opacity: [0.35, 0.65, 0.35],
        }}
        transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
        }}
        style={{
            position: "absolute",
            width: size,
            height: size,
            top,
            left,
            right,
            bottom,
            borderRadius: "50%",
            background:
                `radial-gradient(circle, ${color} 0%, transparent 70%)`,
            pointerEvents: "none",
            zIndex: 1,
        }}
    />
);

const InstitutionalGrid = () => (
    <Box
        sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.065,
            pointerEvents: "none",
            backgroundImage: `
                linear-gradient(
                    rgba(255,255,255,.7) 1px,
                    transparent 1px
                ),
                linear-gradient(
                    90deg,
                    rgba(255,255,255,.7) 1px,
                    transparent 1px
                )
            `,
            backgroundSize: "44px 44px",
            maskImage:
                "linear-gradient(to bottom, black, transparent 90%)",
        }}
    />
);

const ApplyForEntrance = () => {
    const navigate = useNavigate();

    const [showForm, setShowForm] = useState(false);

    return (
        <Box
            sx={{
                minHeight: "100dvh",
                width: "100%",
                overflow: "hidden",
                background: COLORS.cream,
            }}
        >
            <Box
                sx={{
                    minHeight: "100dvh",
                    display: {
                        xs: "block",
                        md: "grid",
                    },
                    gridTemplateColumns: {
                        md: "1.05fr .95fr",
                    },
                }}
            >
                {/* =====================================================
                    LEFT — PASSION AIDS THE FUTURE
                ===================================================== */}

                <Box
                    sx={{
                        display: {
                            xs: "none",
                            md: "flex",
                        },
                        position: "relative",
                        minHeight: "100dvh",
                        overflow: "hidden",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        px: {
                            md: 6,
                            lg: 8,
                            xl: 10,
                        },
                        py: 5,
                        color: COLORS.white,
                        background:
                            "linear-gradient(135deg, #040605 0%, #07100A 48%, #0B3020 100%)",
                    }}
                >
                    <InstitutionalGrid />

                    <Glow
                        size={650}
                        top={-360}
                        left={-300}
                        color="rgba(32,168,74,.19)"
                    />

                    <Glow
                        size={500}
                        bottom={-280}
                        right={-250}
                        color="rgba(245,211,47,.12)"
                    />

                    <FloatingIcon
                        Icon={School}
                        top="18%"
                        left="8%"
                        delay={0}
                        size={31}
                    />

                    <FloatingIcon
                        Icon={EmojiObjects}
                        top="31%"
                        right="11%"
                        delay={1}
                        size={29}
                    />

                    <FloatingIcon
                        Icon={Groups}
                        top="48%"
                        left="9%"
                        delay={2}
                        size={30}
                    />

                    <FloatingIcon
                        Icon={BusinessCenter}
                        top="62%"
                        right="10%"
                        delay={3}
                        size={29}
                    />

                    <FloatingIcon
                        Icon={WorkspacePremium}
                        top="75%"
                        left="20%"
                        delay={4}
                        size={27}
                    />

                    <FloatingIcon
                        Icon={AutoAwesome}
                        top="42%"
                        left="44%"
                        delay={5}
                        size={25}
                    />

                    {/* =================================================
                        LEFT HEADER
                    ================================================= */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                                "space-between",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 50,
                                    height: 50,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent:
                                        "center",
                                    borderRadius: "14px",
                                    background:
                                        "linear-gradient(135deg, #16803A, #20A84A)",
                                    boxShadow:
                                        "0 14px 35px rgba(0,0,0,.35)",
                                }}
                            >
                                <School
                                    sx={{
                                        fontSize: 28,
                                        color:
                                            COLORS.white,
                                    }}
                                />
                            </Box>

                            <Box>
                                <Typography
                                    sx={{
                                        fontSize:
                                            ".68rem",
                                        fontWeight: 950,
                                        letterSpacing: 2,
                                        color:
                                            COLORS.lemon,
                                    }}
                                >
                                    PASSION AIDS
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize:
                                            ".78rem",
                                        fontWeight: 800,
                                        letterSpacing: 1,
                                        color:
                                            "rgba(255,255,255,.76)",
                                    }}
                                >
                                    THE FUTURE
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems:
                                    "center",
                                gap: .8,
                                px: 1.5,
                                py: .75,
                                borderRadius: "30px",
                                border:
                                    "1px solid rgba(200,242,58,.18)",
                                background:
                                    "rgba(200,242,58,.055)",
                                backdropFilter:
                                    "blur(12px)",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: "50%",
                                    background:
                                        COLORS.lemon,
                                    boxShadow:
                                        "0 0 12px rgba(200,242,58,.8)",
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize:
                                        ".59rem",
                                    fontWeight: 900,
                                    letterSpacing: 1,
                                    color:
                                        "rgba(255,255,255,.74)",
                                }}
                            >
                                ADMISSIONS OPEN
                            </Typography>
                        </Box>
                    </Box>

                    {/* =================================================
                        CENTER CONTENT
                    ================================================= */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            maxWidth: 700,
                            mt: -5,
                        }}
                    >
                        <motion.div
                            initial={{
                                opacity: 0,
                                y: 30,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            transition={{
                                duration: .8,
                            }}
                        >
                            <Typography
                                sx={{
                                    display:
                                        "inline-flex",
                                    alignItems:
                                        "center",
                                    gap: .8,
                                    px: 1.6,
                                    py: .8,
                                    mb: 2.6,
                                    borderRadius: "7px",
                                    background:
                                        "rgba(200,242,58,.08)",
                                    border:
                                        "1px solid rgba(200,242,58,.22)",
                                    color:
                                        COLORS.lemon,
                                    fontSize:
                                        ".61rem",
                                    fontWeight: 950,
                                    letterSpacing: 1.8,
                                }}
                            >
                                <AutoAwesome
                                    sx={{
                                        fontSize: 15,
                                    }}
                                />

                                ENTRANCE APPLICATION
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: {
                                        md: "3.65rem",
                                        lg: "4.45rem",
                                        xl: "5.15rem",
                                    },
                                    fontWeight: 1000,
                                    lineHeight: .94,
                                    letterSpacing:
                                        "-3px",
                                    textTransform:
                                        "uppercase",
                                }}
                            >
                                Your
                                <br />
                                Passion
                                <br />

                                <Box
                                    component="span"
                                    sx={{
                                        color:
                                            COLORS.lemon,
                                        display:
                                            "block",
                                    }}
                                >
                                    Has Purpose.
                                </Box>
                            </Typography>

                            <motion.div
                                animate={{
                                    opacity: [
                                        .62,
                                        1,
                                        .62,
                                    ],
                                }}
                                transition={{
                                    duration: 3,
                                    repeat:
                                        Infinity,
                                    ease:
                                        "easeInOut",
                                }}
                            >
                                <Typography
                                    sx={{
                                        maxWidth: 570,
                                        mt: 3,
                                        color:
                                            "rgba(255,255,255,.68)",
                                        fontSize:
                                            ".98rem",
                                        lineHeight:
                                            1.85,
                                    }}
                                >
                                    Take the first step toward
                                    professional growth,
                                    practical skills, leadership,
                                    entrepreneurship and
                                    meaningful opportunity.
                                    Your future can begin with
                                    one application.
                                </Typography>
                            </motion.div>

                            {/* PATHWAY CARDS */}

                            <Box
                                sx={{
                                    display:
                                        "grid",
                                    gridTemplateColumns:
                                        "repeat(2, minmax(0, 1fr))",
                                    gap: 1.15,
                                    maxWidth: 520,
                                    mt: 3.5,
                                }}
                            >
                                {[
                                    {
                                        icon: School,
                                        label:
                                            "Professional Education",
                                    },
                                    {
                                        icon: BusinessCenter,
                                        label:
                                            "Career Opportunity",
                                    },
                                    {
                                        icon: Groups,
                                        label:
                                            "Leadership Development",
                                    },
                                    {
                                        icon: EmojiObjects,
                                        label:
                                            "Entrepreneurship",
                                    },
                                ].map(
                                    ({
                                        icon: Icon,
                                        label,
                                    }) => (
                                        <motion.div
                                            key={label}
                                            whileHover={{
                                                y: -3,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    gap: 1.1,
                                                    px: 1.45,
                                                    py: 1.2,
                                                    minHeight: 50,
                                                    borderRadius:
                                                        "10px",
                                                    background:
                                                        "rgba(255,255,255,.045)",
                                                    border:
                                                        "1px solid rgba(255,255,255,.085)",
                                                }}
                                            >
                                                <Icon
                                                    sx={{
                                                        fontSize:
                                                            19,
                                                        color:
                                                            COLORS.lemon,
                                                    }}
                                                />

                                                <Typography
                                                    sx={{
                                                        fontSize:
                                                            ".68rem",
                                                        fontWeight:
                                                            800,
                                                        lineHeight:
                                                            1.35,
                                                        color:
                                                            "rgba(255,255,255,.79)",
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                            </Box>
                                        </motion.div>
                                    )
                                )}
                            </Box>
                        </motion.div>
                    </Box>

                    {/* =================================================
                        LEFT BOTTOM
                    ================================================= */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                                "space-between",
                            borderTop:
                                "1px solid rgba(255,255,255,.09)",
                            pt: 2.5,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize:
                                    ".61rem",
                                fontWeight: 850,
                                letterSpacing: 1.2,
                                color:
                                    "rgba(255,255,255,.42)",
                            }}
                        >
                            PASSION • EDUCATION • SKILLS
                        </Typography>

                        <Typography
                            sx={{
                                fontSize:
                                    ".61rem",
                                fontWeight: 850,
                                color:
                                    "rgba(255,255,255,.42)",
                            }}
                        >
                            OPPORTUNITY • FUTURE
                        </Typography>
                    </Box>
                </Box>

                {/* =====================================================
                    RIGHT — APPLICATION AREA
                ===================================================== */}

                <Box
                    sx={{
                        position: "relative",
                        minHeight: "100dvh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        px: {
                            xs: 2,
                            sm: 4,
                            md: 4.5,
                            lg: 6,
                        },
                        py: {
                            xs: 4,
                            md: 5,
                        },
                        background:
                            "linear-gradient(150deg, #FFFFFF 0%, #F6FAF5 100%)",
                    }}
                >
                    {/* =================================================
                        IMAGE BACKGROUND
                    ================================================= */}

                    <Box
                        sx={{
                            position:
                                "absolute",
                            inset: 0,
                            zIndex: 0,
                            overflow:
                                "hidden",
                        }}
                    >
                        <Box
                            component="img"
                            src={student}
                            alt="PASSION AIDS THE FUTURE students"
                            sx={{
                                position:
                                    "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit:
                                    "cover",
                                objectPosition:
                                    "center",
                                filter:
                                    "saturate(.82) contrast(.96)",
                            }}
                        />

                        <Box
                            sx={{
                                position:
                                    "absolute",
                                inset: 0,
                                background:
                                    `
                                    linear-gradient(
                                        to top,
                                        rgba(245,211,47,.82) 0%,
                                        rgba(245,211,47,.66) 14%,
                                        rgba(245,211,47,.42) 34%,
                                        rgba(245,211,47,.18) 55%,
                                        rgba(245,211,47,.06) 75%,
                                        rgba(245,211,47,0) 100%
                                    )
                                `,
                            }}
                        />

                        <Box
                            sx={{
                                position:
                                    "absolute",
                                inset: 0,
                                background:
                                    "linear-gradient(90deg, rgba(255,255,255,.30) 0%, rgba(255,255,255,.06) 60%, rgba(7,9,8,.16) 100%)",
                            }}
                        />

                        <Box
                            sx={{
                                position:
                                    "absolute",
                                bottom: 24,
                                left: 24,
                                right: 24,
                                zIndex: 3,
                                display: "flex",
                                alignItems:
                                    "center",
                                justifyContent:
                                    "space-between",
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    gap: .8,
                                }}
                            >
                                <CheckCircle
                                    sx={{
                                        fontSize: 16,
                                        color:
                                            COLORS.black,
                                    }}
                                />

                                <Typography
                                    sx={{
                                        color:
                                            COLORS.black,
                                        fontSize:
                                            ".61rem",
                                        fontWeight: 950,
                                        letterSpacing:
                                            1.1,
                                    }}
                                >
                                    YOUR NEXT CHAPTER STARTS HERE
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    color:
                                        COLORS.black,
                                    fontSize:
                                        ".58rem",
                                    fontWeight: 850,
                                    letterSpacing:
                                        .8,
                                    opacity: .72,
                                }}
                            >
                                PATIF
                            </Typography>
                        </Box>
                    </Box>

                    {/* =================================================
                        APPLICATION CONTENT
                    ================================================= */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            width: "100%",
                            maxWidth: 505,
                            minHeight: {
                                xs: 560,
                                sm: 610,
                            },
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                                "center",
                        }}
                    >
                        <AnimatePresence mode="wait">
                            {!showForm ? (
                                <motion.div
                                    key="start"
                                    initial={{
                                        opacity: 0,
                                        x: 45,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        x: 0,
                                    }}
                                    exit={{
                                        opacity: 0,
                                        x: -45,
                                    }}
                                    transition={{
                                        duration: .55,
                                        ease: "easeInOut",
                                    }}
                                    style={{
                                        width: "100%",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            p: {
                                                xs: 3,
                                                sm: 4,
                                            },
                                            borderRadius:
                                                "24px",
                                            background:
                                                "rgba(255,255,255,.94)",
                                            border:
                                                `1px solid ${COLORS.border}`,
                                            boxShadow:
                                                "0 28px 75px rgba(7,9,8,.18)",
                                            backdropFilter:
                                                "blur(18px)",
                                            textAlign:
                                                "center",
                                        }}
                                    >
                                        <motion.div
                                            animate={{
                                                y: [
                                                    0,
                                                    -6,
                                                    0,
                                                ],
                                            }}
                                            transition={{
                                                duration: 3.5,
                                                repeat:
                                                    Infinity,
                                                ease:
                                                    "easeInOut",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 74,
                                                    height: 74,
                                                    mx: "auto",
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    justifyContent:
                                                        "center",
                                                    borderRadius:
                                                        "20px",
                                                    background:
                                                        `linear-gradient(
                                                            135deg,
                                                            ${COLORS.black},
                                                            ${COLORS.green}
                                                        )`,
                                                    boxShadow:
                                                        "0 18px 35px rgba(7,9,8,.18)",
                                                }}
                                            >
                                                <School
                                                    sx={{
                                                        fontSize: 38,
                                                        color:
                                                            COLORS.lemon,
                                                    }}
                                                />
                                            </Box>
                                        </motion.div>

                                        <Typography
                                            sx={{
                                                mt: 3,
                                                fontSize:
                                                    ".64rem",
                                                fontWeight:
                                                    950,
                                                letterSpacing:
                                                    2,
                                                color:
                                                    COLORS.green,
                                            }}
                                        >
                                            PASSION AIDS THE FUTURE
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 1,
                                                fontSize: {
                                                    xs:
                                                        "2rem",
                                                    sm:
                                                        "2.35rem",
                                                },
                                                fontWeight:
                                                    1000,
                                                lineHeight:
                                                    1.03,
                                                letterSpacing:
                                                    "-1.5px",
                                                color:
                                                    COLORS.black,
                                            }}
                                        >
                                            Begin Your
                                            <Box
                                                component="span"
                                                sx={{
                                                    display:
                                                        "block",
                                                    color:
                                                        COLORS.green,
                                                }}
                                            >
                                                Journey
                                            </Box>
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 1.5,
                                                mx: "auto",
                                                maxWidth:
                                                    380,
                                                color:
                                                    COLORS.muted,
                                                fontSize:
                                                    ".82rem",
                                                lineHeight:
                                                    1.7,
                                            }}
                                        >
                                            Take the first step toward
                                            professional education,
                                            practical skills, leadership
                                            and meaningful opportunity.
                                        </Typography>

                                        <Button
                                            fullWidth
                                            onClick={() =>
                                                setShowForm(
                                                    true
                                                )
                                            }
                                            endIcon={
                                                <ArrowForward />
                                            }
                                            sx={{
                                                mt: 3,
                                                minHeight:
                                                    58,
                                                borderRadius:
                                                    "13px",
                                                textTransform:
                                                    "none",
                                                fontSize:
                                                    ".88rem",
                                                fontWeight:
                                                    950,
                                                color:
                                                    COLORS.black,
                                                background:
                                                    `linear-gradient(
                                                        135deg,
                                                        ${COLORS.lemon} 0%,
                                                        ${COLORS.yellow} 100%
                                                    )`,
                                                boxShadow:
                                                    "0 16px 32px rgba(200,242,58,.24)",
                                                transition:
                                                    "all .25s ease",
                                                "&:hover":
                                                    {
                                                        background:
                                                            `linear-gradient(
                                                                135deg,
                                                                ${COLORS.lemonBright},
                                                                ${COLORS.yellowBright}
                                                            )`,
                                                        transform:
                                                            "translateY(-2px)",
                                                        boxShadow:
                                                            "0 20px 40px rgba(200,242,58,.32)",
                                                    },
                                            }}
                                        >
                                            Start Application
                                        </Button>

<Button
    fullWidth
    onClick={() => navigate("/entranceLogin")}
    endIcon={<ArrowForward />}
    sx={{
        mt: 3,
        minHeight: 58,
        borderRadius: "13px",
        textTransform: "none",
        fontSize: ".88rem",
        fontWeight: 950,
        color: "#FFFFFF",
        background: "#C75B12",
        border: "1px solid #C75B12",
        boxShadow: "0 16px 32px rgba(199,91,18,.24)",
        transition: "all .25s ease",

        "&:hover": {
            background: "#FFFFFF",
            color: "#C75B12",
            borderColor: "#C75B12",
            transform: "translateY(-2px)",
            boxShadow: "0 20px 40px rgba(199,91,18,.28)",

            "& .MuiButton-endIcon": {
                color: "#C75B12",
            },
        },

        "& .MuiButton-endIcon": {
            color: "#FFFFFF",
            transition: "color .25s ease",
        },
    }}
>
    View Entrance Information
</Button>

                                        <Button
                                            fullWidth
                                            onClick={() =>
                                                navigate(
                                                    "/"
                                                )
                                            }
                                            sx={{
                                                mt: .8,
                                                color:
                                                    COLORS.muted,
                                                fontSize:
                                                    ".72rem",
                                                fontWeight:
                                                    800,
                                                textTransform:
                                                    "none",
                                                "&:hover":
                                                    {
                                                        background:
                                                            COLORS.paleGreen,
                                                        color:
                                                            COLORS.black,
                                                    },
                                            }}
                                        >
                                            Return to PASSION AIDS THE FUTURE
                                        </Button>
                                    </Box>

                                    <Box
                                        sx={{
                                            mt: 2,
                                            textAlign:
                                                "center",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize:
                                                    ".59rem",
                                                color:
                                                    COLORS.muted,
                                            }}
                                        >
                                            PROFESSIONAL • VOCATIONAL • LEADERSHIP
                                        </Typography>
                                    </Box>
                                </motion.div>
                            ) : (
                                <ApplyForEntranceForm
                                    key="form"
                                    onClose={() =>
                                        setShowForm(
                                            false
                                        )
                                    }
                                />
                            )}
                        </AnimatePresence>
                    </Box>

                    {/* =================================================
                        MOBILE BRAND
                    ================================================= */}

                    <Box
                        sx={{
                            display: {
                                xs: "flex",
                                md: "none",
                            },
                            position: "absolute",
                            top: 17,
                            left: 18,
                            right: 18,
                            zIndex: 20,
                            alignItems:
                                "center",
                            justifyContent:
                                "space-between",
                        }}
                    >
                        <Box
                            sx={{
                                display:
                                    "flex",
                                alignItems:
                                    "center",
                                gap: .9,
                                px: 1,
                                py: .7,
                                borderRadius:
                                    "12px",
                                background:
                                    "rgba(255,255,255,.88)",
                                backdropFilter:
                                    "blur(12px)",
                                boxShadow:
                                    "0 8px 25px rgba(7,9,8,.08)",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 34,
                                    height: 34,
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    borderRadius:
                                        "9px",
                                    background:
                                        COLORS.black,
                                }}
                            >
                                <School
                                    sx={{
                                        color:
                                            COLORS.lemon,
                                        fontSize: 20,
                                    }}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    fontSize:
                                        ".61rem",
                                    fontWeight:
                                        950,
                                    letterSpacing:
                                        .8,
                                    color:
                                        COLORS.black,
                                }}
                            >
                                PASSION AIDS
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                px: 1.25,
                                py: .65,
                                borderRadius:
                                    "20px",
                                background:
                                    COLORS.lemon,
                                color:
                                    COLORS.black,
                                fontSize:
                                    ".55rem",
                                fontWeight:
                                    950,
                                letterSpacing:
                                    .5,
                            }}
                        >
                            ADMISSIONS
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ApplyForEntrance;
